import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ApprovalConfig,
  ApprovalListResponse,
  ApprovalGetResponse,
  AuditLogResponse,
  ApproveResponse,
  RejectResponse,
} from "@property-reports/api-contract";

export function useApprovalConfig() {
  return useQuery<ApprovalConfig>({
    queryKey: ["admin", "approvals", "config"],
    queryFn: async () => {
      const response = await fetch("/api/admin/approvals/config");
      if (!response.ok) throw new Error("Failed to fetch approval config");
      return response.json();
    },
  });
}

export function usePendingBatches() {
  return useQuery<ApprovalListResponse>({
    queryKey: ["admin", "approvals", "batches"],
    queryFn: async () => {
      const response = await fetch("/api/admin/approvals");
      if (!response.ok) throw new Error("Failed to fetch pending batches");
      return response.json();
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

export function useBatchDetail(batchId: string | null) {
  return useQuery<ApprovalGetResponse>({
    queryKey: ["admin", "approvals", "batch", batchId],
    queryFn: async () => {
      const response = await fetch(`/api/admin/approvals/${batchId}`);
      if (!response.ok) throw new Error("Failed to fetch batch details");
      return response.json();
    },
    enabled: !!batchId,
  });
}

export function useReportHtml(batchId: string | null, reportId: string | null) {
  return useQuery<string | null>({
    queryKey: ["admin", "approvals", "html", batchId, reportId],
    queryFn: async () => {
      const response = await fetch(
        `/api/admin/approvals/${batchId}/html/${reportId}`,
      );
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!batchId && !!reportId,
  });
}

export function useBatchAudit(batchId: string | null) {
  return useQuery<AuditLogResponse>({
    queryKey: ["admin", "approvals", "audit", batchId],
    queryFn: async () => {
      const response = await fetch(`/api/admin/approvals/${batchId}/audit`);
      if (!response.ok) throw new Error("Failed to fetch audit log");
      return response.json();
    },
    enabled: !!batchId,
  });
}

export function useApproveBatch() {
  const queryClient = useQueryClient();

  return useMutation<ApproveResponse, Error, string>({
    mutationFn: async (batchId: string) => {
      const response = await fetch(`/api/admin/approvals/${batchId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to approve batch");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "approvals"] });
    },
  });
}

export function useRejectBatch() {
  const queryClient = useQueryClient();

  return useMutation<
    RejectResponse,
    Error,
    { batchId: string; reason: string }
  >({
    mutationFn: async ({ batchId, reason }) => {
      const response = await fetch(`/api/admin/approvals/${batchId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to reject batch");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "approvals"] });
    },
  });
}
